import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button, Card, Input, Spinner, useToast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';

export function SettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await api.get('/admin/settings')).data,
  });

  const [form, setForm] = useState({
    fromName: '',
    replyToEmail: '',
    reminderSendHour: 9,
    reminderLeadDaysEmail: 3,
    reminderLeadDaysSms: 1,
    duesReminderDaysBefore: 7,
    smsEnabled: true,
    emailEnabled: true,
  });

  // Hydrate form from fetched settings the first time data resolves.
  const loadedId = data?.settings?.id as string | undefined;
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  if (loadedId && hydratedFor !== loadedId) {
    setHydratedFor(loadedId);
    setForm({
      fromName: data.settings.fromName ?? '',
      replyToEmail: data.settings.replyToEmail ?? '',
      reminderSendHour: data.settings.reminderSendHour,
      reminderLeadDaysEmail: data.settings.reminderLeadDaysEmail,
      reminderLeadDaysSms: data.settings.reminderLeadDaysSms,
      duesReminderDaysBefore: data.settings.duesReminderDaysBefore,
      smsEnabled: data.settings.smsEnabled,
      emailEnabled: data.settings.emailEnabled,
    });
  }

  const save = useMutation({
    mutationFn: async (body: typeof form) => (await api.patch('/admin/settings', body)).data,
    onSuccess: () => {
      toast.push('Settings saved', 'success');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err) => toast.push(errorMessage(err), 'error'),
  });

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl">Settings</h1>

      <Card className="p-5 space-y-4">
        <h2 className="font-display text-xl">Notification preferences</h2>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            save.mutate(form);
          }}
          className="space-y-4"
        >
          <Input label="From name" value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
          <Input
            type="email"
            label="Reply-to email"
            value={form.replyToEmail}
            onChange={(e) => setForm({ ...form, replyToEmail: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              type="number"
              label="Send hour (local)"
              value={form.reminderSendHour}
              onChange={(e) => setForm({ ...form, reminderSendHour: Number(e.target.value) })}
            />
            <Input
              type="number"
              label="Email lead (days)"
              value={form.reminderLeadDaysEmail}
              onChange={(e) => setForm({ ...form, reminderLeadDaysEmail: Number(e.target.value) })}
            />
            <Input
              type="number"
              label="SMS lead (days)"
              value={form.reminderLeadDaysSms}
              onChange={(e) => setForm({ ...form, reminderLeadDaysSms: Number(e.target.value) })}
            />
          </div>
          <Input
            type="number"
            label="Dues reminder (days before)"
            value={form.duesReminderDaysBefore}
            onChange={(e) => setForm({ ...form, duesReminderDaysBefore: Number(e.target.value) })}
          />
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </Card>

      {/* TODO(section-4): logo upload (POST /admin/settings/logo), Pro branding (POST /admin/settings/branding) */}
    </div>
  );
}
